import Button from '@/components/homepage/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import { subHeadersStyles } from '@/consts/builder';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useEffect, useRef, useState } from 'react';
import { ActionsType } from '@/store/types/IBuilder';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId, getComponentOutlineMargin, isRichTextEmpty } from '@/utils/builderUtils';
import Image from 'next/image';
import RichText from '@/components/RichText/RichText';
import Slider from '@/components/Slider/Slider';
import { CarouselProps, ControlProps, ScrollMode } from 'nuka-carousel';
import LeftNavArrow from '@/components/Carousel/LeftNavArrow';
import RightNavArrow from '@/components/Carousel/RightNavArrow';

type StorytellingProps = {
  actions: ActionsType[];
  anchorLinkID: string;
  backgroundColor: string;
  bigImage: {
    imageActions: ActionsType[];
    alt: string;
    imageTextColor: string;
    eyeText: string;
    horizontalPosition: string;
    href: string;
    src: string;
    title: string;
    titleTag: 'h2' | 'h3' | 'h4';
    verticalPosition: string;
  };
  description?: string;
  eyeBrowText?: string;
  header?: string;
  headerTag: 'h2' | 'h3';
  images: {
    alt: string;
    href?: string;
    price: string;
    productName: string;
    src: string;
    textPosition: string;
  }[];
  isAddBigImageWidth: boolean;
  isBigImageRight: boolean;
  isTwoImageOneLine: boolean;
  margin: boolean;
  outlineMargin: boolean;
  smallImageTextColor: string;
  textAlignment: string;
  textColor: string;
  textPosition: string;
  types: 'Carousel' | 'Feature' | 'Guided';
};
export default function Storytelling({
  actions,
  anchorLinkID,
  backgroundColor,
  bigImage,
  description,
  eyeBrowText,
  header,
  headerTag,
  images,
  isAddBigImageWidth,
  isBigImageRight,
  isTwoImageOneLine,
  margin,
  smallImageTextColor,
  outlineMargin,
  textAlignment,
  textColor,
  textPosition,
  types,
}: StorytellingProps) {
  const isCarousel = types === 'Carousel';
  const isGuided = types === 'Guided';
  const isFeature = types === 'Feature';
  const imageNumber = isGuided ? 4 : 2;
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const HeaderTag = headerTag || 'h3';
  const slicedActions: ActionsType[] =
    actions?.slice(0, 1).map(action => ({
      ...action,
      color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
      mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
    })) || [];
  const [hoverItem, setHoverItem] = useState<number | undefined>(undefined);
  const [sliderHeight, setSliderHeight] = useState<number>(0);
  const imageRef = useRef<any>(null);
  useEffect(() => {
    if (!isCarousel || !imageRef.current) return;
    const resizeSliderHeight = () => {
      setSliderHeight(imageRef.current.offsetHeight || 0);
    };
    resizeSliderHeight();
    window.addEventListener('resize', resizeSliderHeight);
    return () => {
      window.removeEventListener('resize', resizeSliderHeight);
    };
  }, [isCarousel, imageRef?.current?.offsetHeight]);

  const carouselConfig: CarouselProps = {
    carouselId: 'storytelling-carousel',
    cellAlign: 'left',
    cellSpacing: 0,
    className: '',
    disableEdgeSwiping: true,
    dragThreshold: 0.2,
    enableKeyboardControls: true,
    scrollMode: ScrollMode.remainder,
    wrapAround: false,
    renderBottomCenterControls: () => null,
    renderCenterLeftControls: (props: ControlProps) => (
      <LeftNavArrow btnClass="tw-w-9 tw-h-9" containerStyle={{ height: sliderHeight }} item={images[0]} {...props} />
    ),
    renderCenterRightControls: (props: ControlProps) => (
      <RightNavArrow btnClass="tw-w-9 tw-h-9" containerStyle={{ height: sliderHeight }} item={images[0]} {...props} />
    ),
  };

  const getSmallImageTextColor = (href: string | undefined, isHovered: boolean) => {
    if (href && isHovered) return '#0d6363';
    return smallImageTextColor;
  };

  const renderButton = (button: ActionsType[], isAbsolute: boolean = false) => {
    if (!button || button.length === 0) return null;
    return (
      <div className={`tw-pointer-events-auto ${isAbsolute ? 'tw-absolute tw-right-0' : ''}`}>
        {button.map((action, i) => {
          const { color, href, isDropdownButton, label, mobileColor, mobileVariant, variant } = action;

          if (isDropdownButton && action.actions?.length) {
            const { actions: subActions } = action;
            return (
              <DropdownButton
                className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-text-center [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis"
                color={isDesktop ? color : mobileColor}
                key={i}
                label={label}
                options={subActions}
                styles={{
                  fontSize: '14px',
                }}
                type={isDesktop ? variant : mobileVariant}
              />
            );
          }
          return (
            <Button
              className={beTwMerge(
                'tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden',
                mobileVariant === 'link' && 'tw-min-w-0',
                variant === 'link' ? 'large:tw-min-w-0' : 'large:tw-min-w-[146px]'
              )}
              color={isDesktop ? color : mobileColor}
              href={href}
              key={i}
              label={label}
              styles={{
                fontSize: '14px',
              }}
              type={isDesktop ? variant : mobileVariant}
            />
          );
        })}
      </div>
    );
  };

  const renderHeader = () => (
    <div
      className={beTwMerge(
        'tw-relative tw-w-full tw-flex tw-flex-col tw-items-center tw-gap-[32px]',
        textAlignment === 'left' && 'tw-items-start',
        isGuided && 'tw-flex-row tw-justify-between tw-items-center',
        isCarousel && 'large:tw-pr-16'
      )}
    >
      <div
        className={beTwMerge(
          'tw-w-full tw-flex tw-flex-col tw-text-center tw-gap-[8px]',
          textAlignment === 'left' && 'tw-text-left',
          textColor === 'white' && 'tw-text-color-white'
        )}
      >
        {!isGuided && eyeBrowText && (
          <div className="tw-w-full tw-font-base tw-text-[13px] tw-tracking-[0.65px]">{eyeBrowText}</div>
        )}
        {header && (
          <HeaderTag className={beTwMerge('tw-w-full tw-my-0', subHeadersStyles[HeaderTag])}>{header}</HeaderTag>
        )}
        {isFeature && description && !isRichTextEmpty(description) && (
          <RichText className="tw-w-full" content={description} isTextWhite={textColor === 'white'} />
        )}
      </div>
      {(isDesktop || !isGuided) && renderButton(slicedActions, isGuided)}
    </div>
  );
  const renderBigImage = () => {
    const {
      alt,
      imageTextColor,
      eyeText,
      horizontalPosition,
      href,
      imageActions,
      src,
      title,
      titleTag,
      verticalPosition,
    } = bigImage;
    const isImageTextWhite = imageTextColor === 'white';
    const TitleTag = titleTag || 'h2';
    const slicedButton: ActionsType[] =
      imageActions?.slice(0, 1).map(action => ({
        ...action,
        color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
        mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
      })) || [];
    const bigImageAspect = isAddBigImageWidth ? 'tw-aspect-[201/143]' : 'tw-aspect-[164/143]';
    const imageTag = <Image alt={alt || ''} fill src={src || ''} className={`${bigImageAspect} tw-object-cover`} />;
    return (
      <div
        className={beTwMerge(
          'tw-relative tw-w-full large:tw-w-1/2 tw-object-cover',
          isAddBigImageWidth && 'large:tw-w-[60%]',
          bigImageAspect
        )}
      >
        {href ? (
          <a href={href} className={`tw-block tw-w-full tw-h-full ${bigImageAspect} tw-object-cover`}>
            {imageTag}
          </a>
        ) : (
          imageTag
        )}
        <div
          className={beTwMerge(
            'tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-absolute tw-top-0 tw-pointer-events-none tw-gap-[24px] tw-px-8 tw-py-10 tw-justify-center',
            horizontalPosition === 'left' && 'tw-items-start',
            horizontalPosition === 'right' && 'tw-items-end',
            verticalPosition === 'top' && 'tw-justify-start',
            verticalPosition === 'bottom' && 'tw-justify-end',
            isImageTextWhite && 'tw-text-color-white'
          )}
        >
          <div>
            {eyeText && (
              <div className={`tw-text-${horizontalPosition} tw-font-base tw-text-[13px] tw-tracking-[0.65px]`}>
                {eyeText}
              </div>
            )}
            {title && <TitleTag className={`tw-mb-1 tw-mt-0 ${subHeadersStyles[TitleTag]}`}>{title}</TitleTag>}
          </div>
          {renderButton(slicedButton)}
        </div>
      </div>
    );
  };

  const renderImageGallery = () => {
    if (isCarousel) {
      return (
        <div className="tw-w-full">
          <Slider isMobile={!isDesktop} slidesToShow={isDesktop ? 2.5 : 1.5} slidesToScroll={1} {...carouselConfig}>
            {images.map((image, index) => {
              const { alt, href, price, productName, src } = image;
              const imageAspect = isAddBigImageWidth ? 'large:tw-aspect-[95/128]' : 'large:tw-aspect-[119/144]';
              const sliderItem = (
                <div className={`tw-w-full tw-flex tw-flex-col tw-items-center tw-aspect-[119/144] ${imageAspect}`}>
                  <div className={`tw-relative tw-w-full tw-aspect-[119/144] ${imageAspect}`}>
                    <Image alt={alt || ''} fill src={src || ''} ref={imageRef} />
                  </div>
                  <div
                    className={`tw-w-full tw-flex tw-flex-col ${textPosition === 'left' ? 'tw-items-start' : 'tw-items-center'}`}
                    style={{ color: getSmallImageTextColor(href, index === hoverItem) }}
                  >
                    {productName && (
                      <h4 className={`tw-mt-2 tw-mb-0 tw-font-avenirmedium ${subHeadersStyles['h4']}`}>
                        {productName}
                      </h4>
                    )}
                    {price && <div className="tw-font-book tw-text-[14px] tw-leading-[normal]">{price}</div>}
                  </div>
                </div>
              );
              if (href) {
                return (
                  <a
                    href={href}
                    className="tw-block tw-w-full !tw-no-underline tw-h-full !tw-outline-0"
                    onMouseEnter={() => setHoverItem(index)}
                    onMouseLeave={() => setHoverItem(undefined)}
                    onTouchStart={() => setHoverItem(index)}
                    onTouchEnd={() => setHoverItem(undefined)}
                  >
                    {sliderItem}
                  </a>
                );
              }
              return sliderItem;
            })}
          </Slider>
        </div>
      );
    }
    return (
      <div
        className={`tw-w-full tw-flex tw-gap-4 large:tw-gap-6 tw-flex-wrap tw-basis-1/2 ${!isDesktop && isFeature && !isTwoImageOneLine && 'tw-flex-col'}`}
      >
        {images.slice(0, imageNumber).map((image, index) => {
          const { alt, href, price, productName, src } = image;
          const item = (
            <>
              <div
                className={beTwMerge(
                  'tw-relative tw-w-full tw-aspect-square',
                  !isAddBigImageWidth && 'tw-aspect-[358/259] large:tw-aspect-[142/103]'
                )}
              >
                <Image alt={alt || ''} fill src={src || ''} className="tw-object-cover" />
              </div>
              <div
                className={`tw-w-full tw-flex tw-flex-col tw-mt-2 ${textPosition === 'left' ? 'tw-items-start' : 'tw-items-center'}`}
                style={{ color: getSmallImageTextColor(href, index === hoverItem) }}
              >
                {productName && (
                  <h4 className={`tw-font-avenirmedium tw-mb-0 ${subHeadersStyles['h4']}`}>{productName}</h4>
                )}
                {price && <div className="tw-font-book tw-text-[14px] tw-leading-[normal]">{price}</div>}
              </div>
            </>
          );
          let imageWidth = 'tw-w-[calc(50%-12px)]';
          if (!isDesktop) {
            imageWidth = 'tw-w-full';
            if (isTwoImageOneLine) {
              imageWidth = 'tw-w-[calc(50%-8px)]';
            }
          }
          return (
            <div className={`tw-flex tw-flex-col tw-items-center ${imageWidth}`}>
              {href ? (
                <a
                  className="tw-block tw-w-full tw-h-full !tw-no-underline"
                  href={href}
                  onMouseEnter={() => setHoverItem(index)}
                  onMouseLeave={() => setHoverItem(undefined)}
                  onTouchStart={() => setHoverItem(index)}
                  onTouchEnd={() => setHoverItem(undefined)}
                >
                  {item}
                </a>
              ) : (
                item
              )}
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div
      className={beTwMerge(
        margin && 'tw-pb-8 large:tw-py-16',
        isCarousel && margin && 'tw-pb-10',
        getComponentOutlineMargin(outlineMargin)
      )}
      style={{
        backgroundColor,
      }}
      {...getAnchorId(anchorLinkID)}
    >
      <div
        className={`tw-flex tw-items-stretch tw-flex-col ${isCarousel ? 'tw-gap-10' : 'tw-gap-4'} ${isBigImageRight && !isCarousel ? 'large:tw-flex-row-reverse' : 'large:tw-flex-row'} large:tw-gap-0 ${isCarousel ? 'large:tw-pl-16' : 'large:tw-px-16'}`}
      >
        {bigImage && renderBigImage()}
        <div
          className={beTwMerge(
            'tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-8 large:tw-gap-14 large:tw-w-1/2 large:tw-pl-16',
            isAddBigImageWidth && 'large:tw-w-[40%]',
            isGuided && 'tw-gap-4 large:tw-gap-8',
            isCarousel ? 'tw-gap-10 tw-pl-4' : 'tw-px-4 large:tw-pr-0',
            isBigImageRight && !isCarousel && 'large:tw-pl-0 large:tw-pr-16'
          )}
        >
          {renderHeader()}
          {images && renderImageGallery()}
          {!isDesktop && isGuided && renderButton(slicedActions)}
        </div>
      </div>
    </div>
  );
}
